import React, { useEffect, useState } from "react";
import { Collapse, Button, Input } from "antd";
import { info, error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";
import Loading from "../layout/Loading";

const { Panel } = Collapse;

const CommunicationGrid = () => {
    const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loanNumber, setLoanNumber] = useState("");
    const [replyMessages, setReplyMessages] = useState({});
    const [sendingReply, setSendingReply] = useState(null);
    const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    useEffect(() => {
        if (loanNumber) {
            fetchUserDetails();
        }
    }, [loanNumber]);

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async (loanObject) => {
                loanObject.all().then(async (loan) => {
                    setLoanNumber(loan.loan.loanIdNumber);
                    info("Broker communication page loaded", { loanid: loan.id || "N/A" });
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
        }
    };

    const fetchUserDetails = async () => {
        setStatusMessage({ type: "", text: "" }); // Clear previous message
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const user = await tpoAuthObject.getUser();

            if (user) {
                const authCode = await tpoAuthObject.createAuthCode();
                const url = `${API_DOMAIN}/api/support/by-loanNumber?loan=${encodeURIComponent(
                    loanNumber
                )}&authCode=${encodeURIComponent(authCode)}`;

                try {
                    const response = await fetch(url, {
                        method: "GET",
                        headers: { "Content-Type": "application/json" },
                    });
                    if (!response.ok) throw new Error("Failed to fetch messages");
                    const result = await response.json();
                    setData(result);
                } catch (err) {
                    console.error(err);
                } finally {
                    setLoading(false);
                }
            }
        } catch (error) {
            const errorCode = logError("USER_FETCH_ERROR");
            logAppInsightError(`${errorCode} - fetching external user details.`, {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - fetching external user details.`,
                component: "ContactForm",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReplyChange = (id, value) => {
        setReplyMessages((prev) => ({ ...prev, [id]: value }));
    };

    const handleSendReply = async (threadId) => {
        setStatusMessage({ type: "", text: "" }); // Clear previous message
        if (!replyMessages[threadId]?.trim()) {
            setStatusMessage({ type: "error", text: "Please enter a reply message" });
            return;
        }
        setSendingReply(threadId);
        try {
            const url = `${API_DOMAIN}/api/support/resolve`;

            const response = await fetch(url, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: threadId, responseMessage: replyMessages[threadId] }),
            });

            if (!response.ok) throw new Error("Failed to save response");


            setStatusMessage({ type: "success", text: "Response saved successfully!" });
            setReplyMessages((prev) => ({ ...prev, [threadId]: "" }));
            await fetchUserDetails();
        } catch (error) {
            console.error("Reply Error:", error);
            setStatusMessage({ type: "error", text: "Failed to save response" });
        } finally {
            setSendingReply(null);
            setLoading(false);
        }
    };

    const formatDateWithoutTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(); // Only date, no time
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="p-4">
            <style>{`
                    .communication-panel .ant-collapse-header {
                        padding: 12px 16px 12px 40px !important;
                        position: relative;
                    }
                    .communication-panel .ant-collapse-expand-icon {
                        position: absolute;
                        left: 16px;
                        top: 30%;
                        transform: translateY(-50%);
                    }
                    .communication-panel .ant-collapse-header-text {
                        width: 100%;
                    }
            `}</style>
            {data.length === 0 ? (
                <div className="text-center text-gray-500 py-6">
                    No communication threads found.
                </div>
            ) : (
                <Collapse accordion className="communication-panel">
                    {data.map((thread) => (
                        <Panel
                            key={thread.id}
                            header={
                                <div className="w-full">
                                    {/* Top row: Name and Date */}
                                    <div className="flex justify-between items-center w-full">
                                        <span className="font-semibold truncate" style={{ maxWidth: '60%' }}>
                                            {thread.name}
                                        </span>
                                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                            {formatDateWithoutTime(thread.createdDate)}
                                        </span>
                                    </div>

                                    {/* Second row: Truncated message */}
                                    <div className="w-full mt-1">
                                        <p
                                            className="text-gray-700 text-sm truncate w-full"
                                            style={{ maxWidth: 'calc(100% - 20px)' }}
                                            title={thread.message}
                                        >
                                            {thread.message}
                                        </p>
                                    </div>
                                </div>
                            }
                            className="custom-panel"

                        >
                            <div className="bg-gray-50 p-3 rounded mb-3 space-y-3">
                                {/* Contact Info */}
                                <div className="text-sm text-gray-600">
                                    <p>Email: {thread.email}</p>
                                    {thread.phoneNumber && <p>Phone: {thread.phoneNumber}</p>}
                                </div>

                                {/* Original message */}
                                <div>
                                    <p className="font-medium text-gray-800">Message:</p>
                                    <p className="whitespace-pre-wrap">{thread.message}</p>
                                </div>

                                {/* Response section */}
                                {thread.responseMessage && (
                                    <div className="bg-gray-100 p-2 rounded border border-gray-300">
                                        <p className="font-medium text-gray-700">Response:</p>
                                        <p className="whitespace-pre-wrap text-gray-800">
                                            {thread.responseMessage}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Reply Box */}
                            {!thread.isResolved && (
                                <div className="flex flex-col gap-2">
                                    <Input.TextArea
                                        rows={2}
                                        placeholder="Type your reply..."
                                        value={replyMessages[thread.id] || ""}
                                        onChange={(e) => handleReplyChange(thread.id, e.target.value)}
                                    />
                                    {/* 🟩 Message Box BELOW the button */}
                                    {statusMessage.text && (
                                        <div
                                            className={`mt-3 rounded-md p-3 text-sm font-medium ${statusMessage.type === "success"
                                                ? "bg-green-100 text-green-800"
                                                : statusMessage.type === "error"
                                                    ? "bg-red-100 text-red-800"
                                                    : "bg-yellow-100 text-yellow-800"
                                                }`}
                                        >
                                            {statusMessage.text}
                                        </div>
                                    )}
                                    <div className="flex justify-end mt-2">
                                        <Button
                                            type="primary"
                                            size="small"
                                            loading={sendingReply === thread.id}
                                            onClick={() => handleSendReply(thread.id)}
                                            style={{ backgroundColor: "#2080C3", borderColor: "#2080C3" }}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Panel>
                    ))}
                </Collapse>
            )}
        </div>
    );
};

export default CommunicationGrid;
